import React from 'react';
import { Link } from 'react-router-dom';
import { Upload, Search, CheckSquare, Send, ArrowRight, Shield, Clock } from 'lucide-react';

const steps = [
  {
    num: '01',
    icon: Upload,
    title: 'Submit Your Documents',
    subtitle: 'Secure upload in minutes',
    desc: 'Create a free account and upload your tax documents through our encrypted portal. We accept W-2s, 1099s, business records, and more. Our secure document checklist guides you through exactly what\'s needed for your specific situation.',
    details: ['Create your free secure account', 'Upload documents via encrypted portal', 'Follow our guided document checklist', 'Add notes or questions for your advisor', 'Real-time upload confirmation'],
    color: '#0B1F3A'
  },
  {
    num: '02',
    icon: Search,
    title: 'Expert Review & Preparation',
    subtitle: 'Your dedicated tax professional at work',
    desc: 'A credentialed tax professional reviews your documents and prepares your return. We apply a multi-layer review process to ensure accuracy and maximize every deduction and credit available to you.',
    details: ['Dedicated tax professional assigned', 'Line-by-line document review', 'Maximum deductions identified', 'Tax planning recommendations', 'Questions addressed proactively'],
    color: '#2563EB'
  },
  {
    num: '03',
    icon: CheckSquare,
    title: 'Review & Approve',
    subtitle: 'You\'re always in control',
    desc: 'We walk you through your completed return in plain English. Every number is explained. We answer all your questions and ensure you fully understand and agree with your return before anything is filed.',
    details: ['Clear explanation of your return', 'Line-by-line walkthrough available', 'All questions answered', 'Digital signature process', 'Approval at your convenience'],
    color: '#0B1F3A'
  },
  {
    num: '04',
    icon: Send,
    title: 'File & Maximize',
    subtitle: 'Secure e-filing and refund tracking',
    desc: 'After your approval, we e-file your return directly with the IRS and any applicable state agencies. We provide confirmation of acceptance and track your refund status on your behalf.',
    details: ['IRS-authorized e-filing', 'State filing in all 50 states', 'Instant filing confirmation', 'Refund status tracking', 'Post-filing support'],
    color: '#2563EB'
  },
];

const HowItWorksPage = () => (
  <div style={{ paddingTop: '72px' }}>
    <section className="py-16 px-6" style={{ background: '#0B1F3A' }}>
      <div className="max-w-4xl mx-auto text-center">
        <div className="label-uppercase mb-4" style={{ color: '#D4AF37' }}>The Process</div>
        <h1 className="font-heading text-5xl font-extrabold text-white tracking-tight mb-6">How It Works</h1>
        <p className="text-blue-200 text-xl max-w-2xl mx-auto">
          Simple, transparent, and stress-free. From document submission to filed return in four easy steps.
        </p>
      </div>
    </section>

    {steps.map((step, i) => {
      const Icon = step.icon;
      return (
        <section key={step.num} className={`py-20 px-6 ${i % 2 === 0 ? 'bg-white' : 'bg-[#F8FAFC]'}`}>
          <div className="max-w-7xl mx-auto">
            <div className={`grid lg:grid-cols-2 gap-16 items-center ${i % 2 !== 0 ? '' : ''}`}>
              <div>
                <div className="font-heading text-8xl font-extrabold opacity-10 mb-4 leading-none" style={{ color: step.color }}>{step.num}</div>
                <div className="flex items-center gap-3 mb-5">
                  <div className="w-12 h-12 rounded-xl flex items-center justify-center" style={{ background: step.color }}>
                    <Icon size={24} className="text-white" />
                  </div>
                  <div>
                    <div className="font-heading font-bold text-[#0B1F3A] text-xl">{step.title}</div>
                    <div className="text-[#475569] text-sm">{step.subtitle}</div>
                  </div>
                </div>
                <p className="text-[#475569] text-lg leading-relaxed mb-8">{step.desc}</p>
                <ul className="space-y-3">
                  {step.details.map((d) => (
                    <li key={d} className="flex items-center gap-3">
                      <div className="w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0" style={{ background: `${step.color}20` }}>
                        <div className="w-2 h-2 rounded-full" style={{ background: step.color }} />
                      </div>
                      <span className="text-[#475569] text-sm">{d}</span>
                    </li>
                  ))}
                </ul>
              </div>
              <div className={`p-10 rounded-2xl ${i % 2 !== 0 ? 'lg:order-first' : ''}`} style={{ background: `${step.color}08` }}>
                <div className="text-center">
                  <div className="w-24 h-24 rounded-2xl flex items-center justify-center mx-auto mb-6" style={{ background: step.color }}>
                    <Icon size={48} className="text-white" />
                  </div>
                  <div className="font-heading text-6xl font-extrabold mb-2" style={{ color: step.color }}>{step.num}</div>
                  <div className="font-heading font-bold text-[#0B1F3A] text-2xl">{step.title}</div>
                </div>
              </div>
            </div>
          </div>
        </section>
      );
    })}

    {/* Trust Bar */}
    <section className="py-12 px-6 bg-white border-t border-gray-100">
      <div className="max-w-4xl mx-auto">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
          {[
            { icon: Shield, label: 'IRS Authorized', sub: 'E-File Provider' },
            { icon: Clock, label: 'Fast Turnaround', sub: '3-5 business days' },
            { icon: CheckSquare, label: 'Multi-Layer Review', sub: 'Before every filing' },
            { icon: Send, label: 'All 50 States', sub: 'Federal + State' },
          ].map(({ icon: Icon, label, sub }) => (
            <div key={label} className="text-center">
              <div className="w-12 h-12 rounded-xl flex items-center justify-center mx-auto mb-3" style={{ background: '#0B1F3A10' }}>
                <Icon size={24} style={{ color: '#0B1F3A' }} />
              </div>
              <div className="font-semibold text-[#0B1F3A] text-sm">{label}</div>
              <div className="text-[#94A3B8] text-xs">{sub}</div>
            </div>
          ))}
        </div>
      </div>
    </section>

    <section className="py-20 px-6" style={{ background: '#0B1F3A' }}>
      <div className="max-w-3xl mx-auto text-center">
        <h2 className="font-heading text-4xl font-bold text-white tracking-tight mb-6">Ready to Get Started?</h2>
        <p className="text-blue-200 text-lg mb-10">Book your free consultation and we'll walk you through the entire process.</p>
        <Link to="/book-appointment">
          <button className="inline-flex items-center gap-2 px-10 py-4 rounded-full font-bold text-[#0B1F3A] hover:shadow-xl transition-all duration-300" style={{ background: '#D4AF37' }}>
            Book Free Consultation <ArrowRight size={18} />
          </button>
        </Link>
      </div>
    </section>
  </div>
);

export default HowItWorksPage;
